/**
 * Pure rules of the one task form (T-46, PRD §6.5 / §8): validation and the
 * payload builders. The edit builders reproduce, key for key, the objects the
 * old forms sent; the create body is placed by the engagement since P-02b
 * (D-26). Pinned by `src/utils/__tests__/taskFormModel.test.ts`.
 *
 * Canonical direction (engagement → milestone → task, D-26): the task form
 * never creates milestones. It may place the new task under an EXISTING
 * milestone of its project, in the same create request (`milestone_id`).
 */
import type { TaskAssigneesPayload } from '../tasks/taskAssigneesModel';
import { validateChain, type ChainValue } from '../engagements/engagementChainModel';

export interface TaskCreateValues {
  name: string;
  startDate: string;
  endDate: string;
}

/**
 * `api.createTicket` body of every "new task" form (global, per project, per
 * engagement, per customer). P-02b (D-26): ONE request placed by the
 * ENGAGEMENT (`engagement_id`) and, when picked, the milestone
 * (`milestone_id`); the server fills the project of a project / development.
 */
export function buildCreateTaskPayload(input: {
  customerId: string;
  name: string;
  organizationId: string;
  assignees: TaskAssigneesPayload;
  endDate: string;
  engagementId: string;
  /** '' = no milestone (directly under the project, or no milestones at all). */
  milestoneId?: string;
  startDate: string;
  /** P-06/A (D-57) — the rest of the form; each key only when given. */
  description?: string;
  /** D-328 — optional; blank = not sent (NULL). When given it is > 0. */
  estimatedHours?: string;
  priority?: string;
  status?: string;
  /** D-59 — "כן" to "לשייך גם לאבן הדרך ולפרויקט?". */
  alsoAssignParents?: boolean;
  /** P-37 (D-194 Q-179) — "יצירת משימה מהקריאה": the ticket it comes from. */
  sourceTicketId?: string;
}) {
  return {
    customer_id: input.customerId,
    title: input.name.trim(),
    task_type: 'task' as const,
    organization_id: input.organizationId,
    // P-22 (D-156 #4) — no owner on a form that was emptied on purpose is sent as '' so the
    // server does not fall back to the creator (the key absent = the creator owns the task).
    assigned_agent_id: input.assignees.assigned_agent_id ?? '',
    additional_agent_ids: input.assignees.additional_agent_ids,
    due_date: input.endDate || undefined,
    engagement_id: input.engagementId,
    ...(input.milestoneId ? { milestone_id: input.milestoneId } : {}),
    start_date: input.startDate || undefined,
    ...(input.description?.trim() ? { description: input.description.trim() } : {}),
    ...(input.estimatedHours !== undefined && input.estimatedHours.trim() !== '' ? { estimated_hours: Number(input.estimatedHours) } : {}),
    ...(input.priority ? { priority: input.priority } : {}),
    ...(input.status ? { status: input.status } : {}),
    ...(input.alsoAssignParents ? { also_assign_parents: true } : {}),
    ...(input.sourceTicketId ? { source_ticket_id: input.sourceTicketId } : {}),
  };
}

/**
 * P-22 (D-156 #4, updates D-57 / T-08ב) — "אחראי" of a NEW task is pre-filled with
 * the creator, for every kind of user. Once the user touches the field their pick
 * wins, including an empty one (cleared on purpose).
 */
export function newTaskOwnerId(input: { touched: boolean; pickedOwnerId: string; creatorId?: string }): string {
  return input.touched ? input.pickedOwnerId : (input.creatorId ?? '');
}

/**
 * P-06/A — the full edit of a task (the same fields as the new-task form,
 * the engagement chain excepted: it is fixed, D-37). The status is sent only
 * when it changed (a completed task reopens through its own action, B.23).
 */
export function buildFullEditTaskPayload(input: {
  name: string;
  description: string;
  ownerId: string;
  assigneeIds: readonly string[];
  startDate: string;
  dueDate: string;
  estimatedHours: string;
  priority: string;
  status: string;
  initialStatus: string;
  alsoAssignParents?: boolean;
}) {
  const ids = Array.from(new Set([...(input.ownerId ? [input.ownerId] : []), ...input.assigneeIds.filter(Boolean)]));
  return {
    title: input.name.trim(),
    description: input.description,
    priority: input.priority,
    start_date: input.startDate || null,
    due_date: input.dueDate || null,
    // D-328 — blank clears the estimate (NULL), never 0.
    estimated_hours: input.estimatedHours.trim() === '' ? null : Number(input.estimatedHours),
    assigned_agent_id: input.ownerId || ids[0] || null,
    agent_ids: ids,
    ...(input.status && input.status !== input.initialStatus ? { status: input.status } : {}),
    ...(input.alsoAssignParents ? { also_assign_parents: true } : {}),
  };
}

/** D-328 — the estimate field: optional; when filled, a number above zero. */
export function estimateError(value: string | undefined, t: Translate): string | undefined {
  if (value === undefined || value.trim() === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return t('taskForm.errors.estimateRequired');
  return undefined;
}

/** Edit from the tasks list (name + due date). */
export function buildListEditTaskPayload(input: { name: string; dueDate: string }) {
  return {
    title: input.name.trim(),
    due_date: input.dueDate || (null as unknown as string),
  };
}

/**
 * Edit from the project page (name, owner, due date, and the start date, B4).
 * `start_date` is in the body only when it changed (`null` clears it), so an
 * edit that leaves it alone sends exactly the old body.
 */
export function buildProjectEditTaskPayload(input: {
  name: string;
  ownerId: string;
  dueDate: string;
  /** The start date in the form (`YYYY-MM-DD` or ''). */
  startDate?: string;
  /** The start date the form opened with (`dateInputValue` of the task's). */
  initialStartDate?: string;
}) {
  return {
    title: input.name.trim(),
    assigned_agent_id: input.ownerId ? input.ownerId : null,
    due_date: input.dueDate || null,
    ...startDatePatch(input.initialStartDate ?? '', input.startDate ?? input.initialStartDate ?? ''),
  };
}

/** `{ start_date }` when the start date changed (null = cleared), else nothing. */
export function startDatePatch(initial: string, current: string): { start_date?: string | null } {
  if ((current || '') === (initial || '')) return {};
  return { start_date: current || null };
}

/**
 * The value of a `<input type="date">` for a DATE the API sends. The API
 * serializes a DATE as midnight of the server's zone: "2026-05-20T00:00:00.000Z"
 * on a UTC server (DEV today), "2026-05-19T21:00:00.000Z" on an Israel-time
 * one. A bare `YYYY-MM-DD` or a UTC midnight keeps its date; any other
 * timestamp becomes the viewer's local calendar day. '' when empty or unreadable.
 * (The old edit form put the raw timestamp in the input, which shows nothing.)
 */
export function dateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z$/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * Field errors for the task form (validated on blur and on submit). Keys are
 * the control ids, so `focusField(firstErrorKey(...))` lands on the field.
 */
export function validateTaskForm(
  v: TaskCreateValues & {
    /** Create: the customer → type → engagement chain (all three required). */
    chain?: ChainValue;
    /** P-06/A (D-19) — the estimate, when the form has it (optional, > 0 when given - D-328). */
    estimatedHours?: string;
  },
  t: Translate,
): Record<string, string | undefined> {
  const errors: Record<string, string | undefined> = v.chain ? validateChain(v.chain, t, 'task') : {};
  if (!v.name.trim()) errors['task-name'] = t('taskForm.errors.nameRequired');
  if (v.startDate && v.endDate && v.startDate > v.endDate) errors['task-end'] = t('taskForm.errors.dateOrder');
  const est = estimateError(v.estimatedHours, t);
  if (est) errors['task-hours'] = est;
  return errors;
}

/** The visual order of the form (D-57), for the error summary and first-error focus. */
export function taskFieldOrder(): string[] {
  return ['task-name', 'task-customer', 'task-type', 'task-engagement', 'task-milestone', 'task-start', 'task-end', 'task-hours'];
}

/**
 * P-02a/C2 — the milestones a picker offers: archived ones are left out, except
 * the one already chosen (so an existing link still shows its name).
 */
export function pickableMilestones<T extends { id: string; archived_at?: string | null }>(milestones: readonly T[], keepId?: string | null): T[] {
  return milestones.filter((m) => !m.archived_at || (!!keepId && m.id === keepId));
}
